import { Zap, Bug, Sparkles, ArrowUpCircle } from "lucide-react";

const entries = [
  {
    version: "0.5.0",
    date: "March 28, 2025",
    tag: "Latest",
    tagColor: "#34a853",
    changes: [
      { type: "feature", icon: Sparkles, text: "AI Code Assist beta — intelligent completions powered by the latest models" },
      { type: "feature", icon: Sparkles, text: "Branch preview deployments — every PR gets its own URL" },
      { type: "improvement", icon: ArrowUpCircle, text: "Reduced cold start times by 60% across all regions" },
      { type: "fix", icon: Bug, text: "Fixed WebSocket disconnects in the live terminal" },
    ],
  },
  {
    version: "0.4.2",
    date: "March 12, 2025",
    changes: [
      { type: "feature", icon: Sparkles, text: "Custom domain support with automatic SSL provisioning" },
      { type: "improvement", icon: ArrowUpCircle, text: "Dashboard redesign — faster navigation, cleaner layout" },
      { type: "fix", icon: Bug, text: "Resolved build cache invalidation issues for monorepos" },
    ],
  },
  {
    version: "0.4.0",
    date: "February 20, 2025",
    changes: [
      { type: "feature", icon: Sparkles, text: "Team workspaces — collaborate with up to 5 members on Pro" },
      { type: "feature", icon: Sparkles, text: "Environment variable management in the dashboard" },
      { type: "improvement", icon: ArrowUpCircle, text: "Build times reduced by 40% with improved caching" },
      { type: "fix", icon: Bug, text: "Fixed deployment rollback for projects with large assets" },
    ],
  },
  {
    version: "0.3.0",
    date: "January 25, 2025",
    changes: [
      { type: "feature", icon: Sparkles, text: "Global edge network — 300+ locations live" },
      { type: "feature", icon: Sparkles, text: "CLI v1.0 release — manage everything from your terminal" },
      { type: "improvement", icon: ArrowUpCircle, text: "Improved error messages for failed deployments" },
    ],
  },
  {
    version: "0.2.0",
    date: "December 10, 2024",
    changes: [
      { type: "feature", icon: Sparkles, text: "Git integration — push to deploy from GitHub and GitLab" },
      { type: "feature", icon: Sparkles, text: "Real-time deployment logs in the dashboard" },
      { type: "fix", icon: Bug, text: "Fixed timeout issues for large Docker builds" },
    ],
  },
];

const typeColors = {
  feature: "#ff9a4d",
  improvement: "#4285f4",
  fix: "#5f6368",
};

const ChangelogPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="changelog-page">
      {/* Hero */}
      <div className="bg-[#f8f9fa] border-b border-[#e8eaed]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-[12px] font-bold text-[#ff9a4d] tracking-wide uppercase mb-4">Changelog</p>
            <h1 className="text-[32px] sm:text-[40px] lg:text-[48px] font-normal text-[#202124]" data-testid="changelog-title">What's new</h1>
            <p className="mt-4 text-base sm:text-lg text-[#5f6368] max-w-xl">
              All the latest updates, improvements, and fixes to Eamico.
            </p>
          </div>
        </div>
      </div>

      {/* Entries */}
      <div className="max-w-[1440px] mx-auto px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="max-w-3xl mx-auto space-y-0" data-testid="changelog-entries">
          {entries.map((entry, i) => (
            <div key={entry.version} className="relative flex gap-6 sm:gap-10" data-testid={`changelog-entry-${i}`}>
              {/* Timeline */}
              <div className="hidden sm:flex flex-col items-center pt-1">
                <div className={`w-3 h-3 rounded-full shrink-0 ${entry.tag ? "bg-[#34a853]" : "bg-[#dadce0]"}`} />
                {i < entries.length - 1 && <div className="w-px h-full bg-[#e8eaed]" />}
              </div>

              {/* Content */}
              <div className="pb-12 sm:pb-16 flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <h3 className="text-[20px] font-medium text-[#202124]">v{entry.version}</h3>
                  {entry.tag && (
                    <span className="px-2.5 py-0.5 text-[11px] font-bold tracking-wide uppercase rounded-full text-white" style={{ backgroundColor: entry.tagColor }}>
                      {entry.tag}
                    </span>
                  )}
                </div>
                <p className="text-[13px] text-[#9aa0a6] mb-5">{entry.date}</p>

                <ul className="space-y-3">
                  {entry.changes.map((change, j) => {
                    const Icon = change.icon;
                    return (
                      <li key={j} className="flex items-start gap-3">
                        <Icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: typeColors[change.type] }} />
                        <span className="text-[14px] text-[#202124] leading-relaxed">{change.text}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;
